import React, {useEffect} from "react";
import {
    Box,
    Button,
    CheckBox,
    Form,
    FormGroup,
    SubmitButton,
    TextInput,
    Text,
    Stack,
} from "@unibuddy/patron";
import * as yup from "yup";
import {useAnalytics} from "@unibuddy/tracking";

import {FormikHelpers} from "formik";
import {PRIVACY_POLICY, TERMS_AND_CONDITIONS} from "../../utils/constants";
import {events} from "../../../../../../analytics/productSpecification";
import {useUniversityContext} from "../../../../../../providers/UniversityProvider";

export const EMAIL_COLLECTION_POPUP = "Email Collection Popup";

type ConversationLeadProps = {
    handleSubmit: (email: string, ubConsent: boolean) => Promise<void>;
    handleDismiss: () => void;
};

type FormValues = {
    email: string;
    privacyChecked?: boolean;
};

export function ConversationLeadForm({handleSubmit, handleDismiss}: ConversationLeadProps) {
    const {trackEvent} = useAnalytics();
    const {universitySlug} = useUniversityContext();

    useEffect(() => {
        trackEvent(events.emailCollectionVisible.name, {product: "Assistant", universitySlug});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Form
            initialValues={{email: "", privacyChecked: false}}
            validationSchema={{
                email: yup.string().email().required(),
                privacyChecked: yup.boolean().required(),
            }}
            onSubmit={async (values: FormValues, {setSubmitting}: FormikHelpers<FormValues>) => {
                setSubmitting(true);
                trackEvent(events.emailCollectionSubmitButtonClicked.name, {
                    product: "Assistant",
                    cta: "Submit",
                    where: EMAIL_COLLECTION_POPUP,
                    universitySlug,
                });
                try {
                    const ubConsent = values.privacyChecked ?? false;
                    if (!ubConsent) {
                        setSubmitting(false);
                        return;
                    }
                    await handleSubmit(values.email, ubConsent);
                } catch (error) {
                    console.error(error);
                    setSubmitting(false);
                }
            }}
        >
            {(props: {values: FormValues; isSubmitting: boolean}) => {
                const {values, isSubmitting} = props;
                return (
                    <Stack space="small">
                        <Box>
                            <Text align="center" weight="700">
                                {
                                    "Submit your email below so that we can follow up on your chat today"
                                }
                            </Text>
                        </Box>

                        <Box>
                            <FormGroup label="Email address" id="email">
                                <TextInput
                                    required
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    value={values.email ?? ""}
                                />
                            </FormGroup>

                            <FormGroup id="privacypolicy">
                                <CheckBox id="privacy" name="privacyChecked">
                                    <Text size="small">
                                        {"By checking the box, you agree to our "}
                                        <a
                                            href={TERMS_AND_CONDITIONS}
                                            rel="noreferrer noopener"
                                            target="_blank"
                                        >
                                            Terms & Conditions
                                        </a>
                                        {" and "}
                                        <a
                                            href={PRIVACY_POLICY}
                                            rel="noreferrer noopener"
                                            target="_blank"
                                        >
                                            Privacy Policy
                                        </a>
                                        {
                                            " and consent to receive marketing emails from this institution"
                                        }
                                    </Text>
                                </CheckBox>
                            </FormGroup>
                        </Box>

                        <Box gap="small" display="flex">
                            <Button
                                color="default"
                                ghost
                                block
                                size="sm"
                                onClick={() => {
                                    trackEvent(events.emailCollectionSkipButtonClicked.name, {
                                        product: "Assistant",
                                        cta: "Skip",
                                        where: EMAIL_COLLECTION_POPUP,
                                        universitySlug,
                                    });
                                    handleDismiss();
                                }}
                                disabled={isSubmitting}
                            >
                                Skip
                            </Button>
                            <SubmitButton
                                color="primary"
                                block
                                size="sm"
                                disabled={values.email === "" || !values.privacyChecked}
                            >
                                Submit
                            </SubmitButton>
                        </Box>
                    </Stack>
                );
            }}
        </Form>
    );
}
